import { useState, useEffect } from 'react';
import Nav from './Nav.jsx';
import { Hero, Work, About, Contact, Footer } from './Sections.jsx';
import CaseStudy from './CaseStudy.jsx';
import CopticCase from './CopticCase.jsx';
import AuthGate from './AuthGate.jsx';
import Seo from './Seo.jsx';
import { PROJECTS, CASES, ACCENTS } from './data.js';
import { parsePath } from './router.js';

function useRoute(initialUrl) {
  const [route, setRoute] = useState(() => {
    if (initialUrl != null) return parsePath(initialUrl);
    if (typeof window !== 'undefined') return parsePath(window.location.pathname);
    return parsePath('/');
  });
  useEffect(() => {
    const on = () => {
      setRoute(parsePath(window.location.pathname));
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', on);
    return () => window.removeEventListener('popstate', on);
  }, []);
  return route;
}

function Home() {
  const personLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Ryan Marfone',
    jobTitle: 'Fractional Chief Product Designer',
    url: 'https://ryanmarfone.com',
    image: 'https://ryanmarfone.com/ryan.jpg',
    sameAs: ['https://www.linkedin.com/in/ryanmarfone'],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'The Woodlands',
      addressRegion: 'TX',
      addressCountry: 'US',
    },
    worksFor: {
      '@type': 'Organization',
      name: 'Independent · Fractional engagements',
    },
  };
  return (
    <>
      <Seo
        title="Fractional Chief Product Designer"
        description="Twenty years shaping retail, commerce, healthcare, and AI experiences. Fractional CPD engagements and senior design leadership."
        path="/"
        type="website"
        jsonLd={personLd}
      />
      <Hero />
      <Work />
      <About />
      <Contact />
      <Footer />
    </>
  );
}

export default function App({ url } = {}) {
  const route = useRoute(url);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
    document.documentElement.style.setProperty('--accent', ACCENTS.clay);
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.style.opacity = 1;
          e.target.style.transform = 'translateY(0)';
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    const sel = '.sec-head, .featured__card, .work__row, .press__row, .cs-section, .cs-metric, .cs-artifact, .cs-pull';
    document.querySelectorAll(sel).forEach((el, i) => {
      el.style.opacity = 0;
      el.style.transform = 'translateY(20px)';
      el.style.transition = `opacity 0.7s cubic-bezier(.2,.8,.2,1) ${Math.min(i * 0.02, 0.25)}s, transform 0.7s cubic-bezier(.2,.8,.2,1) ${Math.min(i * 0.02, 0.25)}s`;
      io.observe(el);
    });
    return () => io.disconnect();
  }, [route]);

  let content;
  let routeSeo = null;
  if (route.name === 'case') {
    const proj = PROJECTS.find(p => p.id === route.slug);
    const gated = !!(proj && proj.gated);
    const caseData = CASES[route.slug];
    const heroImage = caseData?.tile?.image || proj?.tile?.image;
    const description = caseData?.subtitle || proj?.summary || '';
    const title = caseData?.title || proj?.title || 'Case study';
    const path = `/case/${route.slug}`;
    const creativeWorkLd = !gated && caseData ? {
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      name: title,
      creator: { '@type': 'Person', name: 'Ryan Marfone' },
      description,
      image: heroImage ? `https://ryanmarfone.com${heroImage}` : undefined,
      url: `https://ryanmarfone.com${path}`,
      datePublished: proj?.year || undefined,
    } : null;
    routeSeo = (
      <Seo
        title={title}
        description={description}
        image={heroImage}
        path={path}
        gated={gated}
        type="article"
        jsonLd={creativeWorkLd}
      />
    );
    const page = route.slug === 'coptic'
      ? <CopticCase data={CASES.coptic} />
      : <CaseStudy slug={route.slug} />;
    content = gated ? <AuthGate>{page}</AuthGate> : page;
  } else {
    content = <Home />;
  }

  return (
    <>
      {routeSeo}
      <Nav route={route} />
      {content}
    </>
  );
}
